// İSG uzmanı portalı — izin penceresi (`validityState`) + kanıt şeridi sıralaması, TEK yerde
// (S32f, `docs/reviews/S32e-evidence-and-layout.md` §14-§17/§21.2). `VerdictCard.tsx` VE
// `EntryDetailModal.tsx` bu dosyayı PAYLAŞIR — iki yüzeyin `hasLiveMismatch` (G1) gibi
// AYRIŞMAMASI için tek doğruluk kaynağı burada; sıralama/ton kuralı da AYNI risk taşır ve
// AYNI yöntemle kapatılır.
//
// **`entryPermitted` bir HİBEDİR, `validityState` bir PENCEREDİR — ikisi ÇÖKERTİLMEZ**
// (api-contract §29.5, S32f normatif not). Yeşil "giriş izni var" YALNIZ pencere `Current`
// VE hibe `true` iken. Diğer üç birleşim NÖTR bir tarih sözcüğü taşır (`ileri tarihli` /
// `süresi geçmiş`) — kırmızı da YANLIŞ olurdu: red zaten "giriş izni yok"un anlamı, ikinci bir
// anlam kazanırsa guard'ı yanlış telefon görüşmesine yollar (S32e §16).
//
// **İstemci pencereyi KENDİ SAATİYLE hesaplamaz.** `validityState` sunucudan gelir
// (`GateScanPermitDto.validityState`, kablo değeri `'Current' | 'NotStarted' | 'Ended'`) — bu
// dosya `Date.now()` OKUMAZ, yalnız sunucunun sınıflandırmasını istemcinin okuma-kolaylığı
// isimlerine ÇEVİRİR (api-contract §29.5 normatif not, "mülkiyet" gerekçesi).
//
// **Sıralama (§17) — sabit sıra, ASLA `reason`'a göre değil:** rank 0 (aktif+hibe), 1
// (aktif, hibesiz), 2 (ileri tarihli — kendi İÇİNDE `validityStart` ARTAN yeniden sıralanır,
// §17 madde 3 [taste]), 3 (süresi geçmiş). Sıralı KOVALAR kullanılır — sunucu sırası
// (`validityStart` azalan) 0/1/3 kovalarında BOZULMADAN kalır, `Array#sort`'un kararlılığını
// yeniden TÜRETMEYE gerek KALMAZ.

import type { GateScanPermitDto, TagColor } from '@isgportal/shared';
import { Calendar, CalendarOff, CircleCheck, type LucideIcon, OctagonX } from 'lucide-react';

export type PermitWindow = 'active' | 'future' | 'expired';

/** `validityState` kablo değerini istemcinin okuma-kolaylığı için üç durumlu bir isme çevirir. */
export function permitWindow(permit: GateScanPermitDto): PermitWindow {
  switch (permit.validityState) {
    case 'NotStarted':
      return 'future';
    case 'Ended':
      return 'expired';
    default:
      return 'active';
  }
}

/** §17 — SABİT sıralama anahtarı, artan. */
function permitRank(permit: GateScanPermitDto): 0 | 1 | 2 | 3 {
  const window = permitWindow(permit);
  if (window === 'active') {
    return permit.entryPermitted ? 0 : 1;
  }
  return window === 'future' ? 2 : 3;
}

/**
 * §17 — sıralı KOVALAR: 0/1/3 sunucunun sırasını (`validityStart` azalan) AYNEN korur; 2
 * (ileri tarihli) kendi İÇİNDE `validityStart` ARTAN yeniden sıralanır [taste, §17 madde 3] —
 * "ne zaman açılıyor" sorusunun cevabı SOONEST-FIRST'tür, sunucunun azalan sırası bunu TERSTEN
 * cevaplar. Aynı fonksiyon HER yüzeyde/varyantta çalışır (allowed kart, denied kart, modal) —
 * tek kural, yol-bağımsızlığı yok (§17 madde 4, no-causality guard'ın bir parçası, §20).
 */
export function rankPermits(permits: GateScanPermitDto[]): GateScanPermitDto[] {
  const buckets: GateScanPermitDto[][] = [[], [], [], []];
  for (const permit of permits) {
    buckets[permitRank(permit)]?.push(permit);
  }
  const future = buckets[2] ?? [];
  buckets[2] = [...future].sort(
    (a, b) => new Date(a.validityStart).getTime() - new Date(b.validityStart).getTime(),
  );
  return buckets.flat();
}

/** §19 madde 1 — pencere dışı kırılımı, TÜM liste üzerinden (gizli/kaydırılan parça DEĞİL). */
export function permitWindowCounts(permits: GateScanPermitDto[]): {
  future: number;
  expired: number;
} {
  let future = 0;
  let expired = 0;
  for (const permit of permits) {
    const window = permitWindow(permit);
    if (window === 'future') {
      future += 1;
    } else if (window === 'expired') {
      expired += 1;
    }
  }
  return { future, expired };
}

export type PermitChipTone = 'success' | 'danger' | 'neutral';

export interface PermitChip {
  tone: PermitChipTone;
  icon: LucideIcon;
  /** `t()`'e VERİLECEK anahtar — çeviri BURADA yapılmaz, i18n bileşenin işidir. */
  labelKey: string;
}

/** Ton → düz metin rengi (kartın bare fragmanı) veya `Badge` tonu (modal) — TEK eşleme, iki
 *  tüketici (S32e §16 kontrast notu: `text-success`/`text-danger`/`text-muted` üçü de AA). */
export const PERMIT_CHIP_TEXT_CLASS: Record<PermitChipTone, string> = {
  success: 'text-success',
  danger: 'text-danger',
  neutral: 'text-muted',
};

/** `PermitChipTone` → Carbon `Tag` rengi (S33 Slice 4 — eski app-local `Badge` KALDIRILDI,
 * modal density artık `Tag` kullanır; bare-metin density (kart) `PERMIT_CHIP_TEXT_CLASS`'ta
 * DEĞİŞMEDİ). `EligibilityRow.tone` da AYNI `PermitChipTone`'u taşır — TEK eşleme, iki tüketici. */
export const PERMIT_CHIP_TAG_COLOR: Record<PermitChipTone, TagColor> = {
  success: 'green',
  danger: 'red',
  neutral: 'gray',
};

/**
 * §16 — kart VE modal bu FONKSİYONU paylaşır (normatif tablo): yeşil YALNIZ `active` +
 * `entryPermitted`, kırmızı YALNIZ `active` + hibesiz, diğer İKİSİ (`future`/`expired`) NÖTR.
 * İkon fark katmanı DEĞİL, yedeklilik katmanıdır — sözcük üç durumu ayırt eder.
 */
export function permitChip(permit: GateScanPermitDto): PermitChip {
  const window = permitWindow(permit);
  if (window === 'future') {
    return { tone: 'neutral', icon: Calendar, labelKey: 'security.verdict.permit.future' };
  }
  if (window === 'expired') {
    return { tone: 'neutral', icon: CalendarOff, labelKey: 'security.verdict.permit.expired' };
  }
  return permit.entryPermitted
    ? { tone: 'success', icon: CircleCheck, labelKey: 'security.verdict.permit.entryPermitted' }
    : { tone: 'danger', icon: OctagonX, labelKey: 'security.verdict.permit.entryNotPermitted' };
}
